/*
 * Do not remove or alter the notices in this preamble.
 * This software code regards iO Custom Software.
 * Copyright © 2026 iO The Netherlands B.V. and/or its affiliates.
 * www.iodigital.com All rights reserved. License grant and user rights and obligations
 * according to applicable license agreement. Please contact legal@iodigital.com for
 * questions regarding license and user rights.
 */

import { devtools } from 'zustand/middleware';
import { createStore } from 'zustand/vanilla';

import {
  DebtorProductCollectionDto,
  deleteRemoveDebtorCollectionItem,
  getShowDebtorCollectionForProduct,
  postSaveDebtorCollection,
  putSaveDebtorCollectionItem,
} from '@/api-sdk';
import { globalStorePropertiesStore } from '@/stores';
import {
  GlobalComponentPropertiesTypes,
  LiveStatusEvent,
  LiveStatusEvents,
} from '@/types';

export type CollectionDrawerState = Partial<GlobalComponentPropertiesTypes> & {
  collectionList: DebtorProductCollectionDto[] | null;
  pages: number;
  currentPage: number;
  collectionsChecked: Record<string, boolean>;
  loading: boolean;
  liveUpdateStatus: LiveStatusEvent | undefined;

  updateCollectionPromise: Promise<any> | null;
  updateCollectionController: AbortController | null;

  getCollections: (
    page?: number,
    product?: string,
    showLoading?: boolean,
  ) => Promise<void>;
  createCollection: (collectionName: string, product: string) => void;
  addOrRemoveProductFromCollection: (
    collectionId: string,
    inCollection: boolean,
    collectionName: string,
    product: string,
  ) => Promise<void>;
  setConfig: (config: Partial<CollectionDrawerState>) => void;
};

export const collectionDrawerStore = createStore<CollectionDrawerState>()(
  devtools((set, get) => ({
    ...globalStorePropertiesStore,
    collectionList: null,
    pages: 8,
    currentPage: 1,
    collectionsChecked: {},
    loading: false,
    liveUpdateStatus: undefined,
    updateCollectionPromise: null,
    updateCollectionController: null,
    getCollections: async (page, product, showLoading = true) => {
      const { debtor, locale, country } = get();
      if (!debtor || !product || !locale || !country) return;

      let totalCollections: number = 0;

      try {
        if (showLoading) {
          set({ loading: true }, false, 'SET STATE:loading getCollections');
        }

        const collections = await getShowDebtorCollectionForProduct({
          debtorCode: debtor,
          productCode: product,
          params: {
            page: page || 1,
            pageSize: 8,
            locale: locale,
            country: country,
          },
        });

        totalCollections = collections.total;

        const collectionsChecked: Record<string, boolean> = {};
        collections.collections.forEach((collection, index) => {
          collectionsChecked[index] = collection.inCollection;
        });

        set(
          {
            pages: collections.pages,
            collectionsChecked,
            collectionList: collections.collections,
          },
          false,
          'SET_STATE: collection data getCollections',
        );
      } catch (error) {
        console.error('Error fetching collections', error);
        return;
      } finally {
        set(
          {
            loading: false,
            liveUpdateStatus: {
              eventName: LiveStatusEvents.COLLECTIONS_LOADED,
              data: { totalCollections },
            },
          },
          false,
          'SET_STATE: finally getCollections',
        );
      }
    },

    createCollection: async (collectionName, product) => {
      const { debtor, customerId, currentPage, getCollections } = get();

      if (!customerId || !debtor) return;

      try {
        await postSaveDebtorCollection({
          debtorCode: debtor,
          data: {
            collection_name: collectionName,
            product_code: product,
            customer_id: customerId,
          },
        });
      } catch (error) {
        console.error('Error creating collection', error);
        throw error;
      } finally {
        set(
          {
            liveUpdateStatus: {
              eventName: LiveStatusEvents.COLLECTION_CREATED_AND_SELECTED,
              data: { collectionName: collectionName },
            },
          },
          false,
          'SET_STATE: finally createCollection',
        );
        await getCollections(currentPage, product);
      }
    },

    addOrRemoveProductFromCollection: async (
      collectionId,
      inCollection,
      collectionName,
      product,
    ) => {
      const {
        debtor,
        currentPage,
        updateCollectionController,
        getCollections,
      } = get();
      if (!debtor || !product || !collectionId) return;

      const updateData = {
        debtorCode: debtor,
        collectionId,
        productCode: product,
      };

      if (updateCollectionController !== null) {
        updateCollectionController.abort();
        set(
          {
            updateCollectionPromise: null,
            updateCollectionController: null,
          },
          false,
          'SET_STATE: promise controller reset addOrRemoveProductFromCollection',
        );
      }

      const abortController = new AbortController();
      set(
        { updateCollectionController: abortController },
        false,
        'SET_STATE: updateCollectionController set addOrRemoveProductFromCollection',
      );

      const collectionPromise = inCollection
        ? deleteRemoveDebtorCollectionItem(updateData, {
            signal: abortController.signal,
          })
        : putSaveDebtorCollectionItem(updateData, {
            signal: abortController.signal,
          });

      set(
        {
          updateCollectionPromise: collectionPromise,
        },
        false,
        'SET_STATE: promise controller set addOrRemoveProductFromCollection',
      );

      try {
        await collectionPromise;
      } catch (error) {
        console.error(
          `Error ${inCollection ? 'deleting' : 'adding'} product to collection`,
          error,
        );
      } finally {
        set(
          {
            liveUpdateStatus: {
              eventName: inCollection
                ? LiveStatusEvents.COLLECTION_DELETED
                : LiveStatusEvents.COLLECTION_ADDED,
              data: { collectionName: collectionName },
            },
            updateCollectionPromise: null,
            updateCollectionController: null,
          },
          false,
          'SET_STATE: finally addOrRemoveProductFromCollection',
        );
        await getCollections(currentPage, product, false);
      }
    },

    setConfig: config => set(config, false, 'SET_STATE: setConfig'),
  })),
);
